import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AppFrame, TopBar } from "@/components/Shell";
import { api, resolveImage } from "@/lib/api";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, Mic, X, Sparkles, Loader2 } from "lucide-react";
import CategoryTreePicker from "@/components/CategoryTreePicker";
import { compressImage } from "@/lib/image";

const EMPTY = {
  name: "",
  images: [],
  category_id: "",
  category_name: "",
  brand: "",
  selling_price: "",
  mrp: "",
  description: "",
  barcode: "",
  batch_number: "",
  expiry_date: "",
  manufacturing_date: "",
  sku: "",
  color: "",
  size: "",
  weight: "",
  shade: "",
  net_quantity: "",
  tags: [],
  availability: "in_stock",
  featured: false,
  best_seller: false,
  stock_quantity: "",
  status: "published",
};

const fileToDataURL = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

export default function AddProduct() {
  const nav = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const [form, setForm] = useState(EMPTY);
  const [categories, setCategories] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [saving, setSaving] = useState(false);
  const [previewFiles, setPreviewFiles] = useState([]); // {url, dataURL}
  const recRef = useRef(null);
  const camRef = useRef(null);
  const galleryRef = useRef(null);

  useEffect(() => {
    (async () => {
      const c = await api.get("/categories");
      setCategories(c.data);
      if (isEdit) {
        const p = await api.get(`/products/${id}`);
        setForm({ ...EMPTY, ...p.data });
        setPreviewFiles((p.data.images || []).map((u) => ({ url: u })));
      }
    })();
  }, [id]); // eslint-disable-line

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const handleFiles = async (files) => {
    const arr = Array.from(files || []).slice(0, 4);
    // Compress in parallel — huge speedup vs sequential
    const compressed = await Promise.all(arr.map((f) => compressImage(f)));
    const items = await Promise.all(
      compressed.map(async (f) => ({ file: f, dataURL: await fileToDataURL(f) })),
    );
    setPreviewFiles((prev) => [...prev, ...items]);
    // Immediately run AI extract on new photos
    await runAI({ newImages: items.map((i) => i.dataURL) });
  };

  const removeImage = (idx) => {
    setPreviewFiles((prev) => prev.filter((_, i) => i !== idx));
    setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
  };

  const uploadPending = async () => {
    // Upload all pending items in parallel (was sequential — 4x slower)
    return Promise.all(
      previewFiles.map(async (item) => {
        if (item.url) return item.url;
        const fd = new FormData();
        fd.append("file", item.file);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        return data.url;
      }),
    );
  };

  const runAI = async ({ newImages = [], voice = null } = {}) => {
    const images_base64 = [];
    for (const item of previewFiles) {
      if (item.dataURL) images_base64.push(item.dataURL);
    }
    for (const b of newImages) images_base64.push(b);
    const voice_transcript = voice ?? transcript ?? "";
    if (images_base64.length === 0 && !voice_transcript) return;

    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/extract", { images_base64, voice_transcript });
      const ex = data.extracted || {};
      // Fuzzy category match
      let category_id = form.category_id;
      let category_name = form.category_name;
      if (ex.category_hint) {
        const hint = ex.category_hint.toLowerCase();
        const found = categories.find(
          (c) => hint.includes(c.name.toLowerCase()) || c.name.toLowerCase().includes(hint),
        );
        if (found) { category_id = found.id; category_name = found.name; }
      }
      setForm((f) => ({
        ...f,
        name: ex.name || f.name,
        brand: ex.brand || f.brand,
        selling_price: ex.selling_price ?? f.selling_price,
        mrp: ex.mrp ?? f.mrp,
        description: ex.description || f.description,
        barcode: ex.barcode || f.barcode,
        batch_number: ex.batch_number || f.batch_number,
        expiry_date: ex.expiry_date || f.expiry_date,
        manufacturing_date: ex.manufacturing_date || f.manufacturing_date,
        shade: ex.shade || f.shade,
        color: ex.color || f.color,
        size: ex.size || f.size,
        net_quantity: ex.net_quantity || f.net_quantity,
        sku: ex.sku || f.sku,
        tags: (ex.tags && ex.tags.length ? ex.tags : f.tags) || [],
        category_id,
        category_name,
      }));
      toast.success("AI filled the form");
    } catch (e) {
      // AI is a best-effort helper — don't scare the user when it hiccups.
      toast("Auto-fill unavailable — you can fill fields manually.", { icon: "ℹ️" });
    } finally {
      setAiLoading(false);
    }
  };

  const toggleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("Voice not supported on this browser. Use Chrome on Android.");
      return;
    }
    if (listening) {
      recRef.current?.stop();
      return;
    }
    const r = new SR();
    r.lang = "hi-IN";
    r.interimResults = true;
    r.continuous = false;
    let finalText = "";
    r.onresult = (ev) => {
      let interim = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const res = ev.results[i];
        if (res.isFinal) finalText += res[0].transcript;
        else interim += res[0].transcript;
      }
      setTranscript((finalText + " " + interim).trim());
    };
    r.onend = () => {
      setListening(false);
      const t = (finalText || "").trim();
      setTranscript(t);
      if (t) runAI({ voice: t });
    };
    r.onerror = () => { setListening(false); toast.error("Voice error"); };
    recRef.current = r;
    setListening(true);
    r.start();
  };

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Product name required"); return; }
    setSaving(true);
    try {
      let images;
      try {
        images = await uploadPending();
      } catch (upErr) {
        const detail = upErr.response?.data?.detail || "";
        if (upErr.response?.status === 413 || /too large/i.test(detail)) {
          toast.error("A photo is too large. Please re-add it — we'll compress on retry.");
        } else if (!upErr.response) {
          toast.error("Network error while uploading photos. Check your connection.");
        } else {
          toast.error(detail || "Photo upload failed. Please try again.");
        }
        return;
      }
      const payload = {
        ...form,
        images,
        selling_price: Number(form.selling_price) || 0,
        mrp: form.mrp === "" || form.mrp == null ? null : Number(form.mrp),
        stock_quantity:
          form.stock_quantity === "" || form.stock_quantity == null
            ? null
            : Math.max(0, parseInt(form.stock_quantity, 10) || 0),
      };
      if (isEdit) {
        await api.put(`/products/${id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product added");
      }
      nav("/admin/products");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppFrame>
      <TopBar title={isEdit ? "Edit Product" : "Add Product"} subtitle="Studio" />

      <div className="px-5 mt-4 space-y-5">
        {/* Photo capture */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-500 mb-2">Photos</div>
          <div className="grid grid-cols-4 gap-2">
            {previewFiles.map((item, i) => (
              <div key={i} className="relative aspect-square rounded-xl overflow-hidden bg-zinc-50 border border-black/5">
                <img src={item.dataURL || resolveImage(item.url)} alt="" className="w-full h-full object-cover" />
                <button
                  data-testid={`remove-image-${i}`}
                  onClick={() => removeImage(i)}
                  className="absolute top-1 right-1 bg-white/90 rounded-full w-6 h-6 grid place-items-center press"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
            {previewFiles.length < 4 && (
              <>
                <button
                  data-testid="capture-photo-btn"
                  onClick={() => camRef.current?.click()}
                  className="aspect-square rounded-xl border-2 border-dashed border-black/15 bg-zinc-50 grid place-items-center press"
                >
                  <Camera size={20} strokeWidth={1.6} className="text-neutral-500" />
                </button>
                {previewFiles.length < 3 && (
                  <button
                    data-testid="gallery-btn"
                    onClick={() => galleryRef.current?.click()}
                    className="aspect-square rounded-xl border-2 border-dashed border-black/15 bg-zinc-50 grid place-items-center press text-[10px] text-neutral-500"
                  >
                    Gallery
                  </button>
                )}
              </>
            )}
          </div>
          <input
            ref={camRef} type="file" accept="image/*" capture="environment" multiple
            className="hidden" onChange={(e) => handleFiles(e.target.files)}
          />
          <input
            ref={galleryRef} type="file" accept="image/*" multiple
            className="hidden" onChange={(e) => handleFiles(e.target.files)}
          />
        </div>

        {/* Voice */}
        <div className="rounded-3xl border border-black/5 bg-white p-4 flex items-center gap-4">
          <motion.button
            data-testid="voice-toggle-btn"
            onClick={toggleVoice}
            animate={listening ? { scale: [1, 1.08, 1] } : { scale: 1 }}
            transition={{ repeat: listening ? Infinity : 0, duration: 1.4 }}
            className={`w-14 h-14 rounded-full grid place-items-center ${listening ? "bg-[#FADADD] voice-pulse" : "bg-neutral-900 text-white"}`}
          >
            <Mic size={22} strokeWidth={1.6} className={listening ? "text-neutral-900" : ""} />
          </motion.button>
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg leading-tight">Voice Entry</div>
            <div className="text-[11px] text-neutral-500 mt-0.5">
              {listening ? "Listening... speak in Hindi or English" : (transcript ? `"${transcript.slice(0, 60)}..."` : "Tap and describe your product")}
            </div>
          </div>
        </div>

        {/* AI status */}
        <AnimatePresence>
          {aiLoading && (
            <motion.div
              initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              className="rounded-2xl bg-[#FADADD]/50 border border-[#FADADD] p-3 flex items-center gap-3 text-sm"
            >
              <Loader2 size={16} className="animate-spin" />
              <span>AI is reading your product...</span>
              <Sparkles size={14} className="ml-auto" />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Form */}
        <div className="space-y-3">
          <Field label="Product name" testid="field-name">
            <input
              data-testid="input-name"
              value={form.name}
              onChange={(e) => update({ name: e.target.value })}
              className="w-full h-11 rounded-xl border border-black/10 px-3 text-sm outline-none focus:border-neutral-900"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Brand">
              <input data-testid="input-brand" value={form.brand || ""} onChange={(e) => update({ brand: e.target.value })}
                className="w-full h-11 rounded-xl border border-black/10 px-3 text-sm outline-none focus:border-neutral-900" />
            </Field>
            <Field label="Category">
              <CategoryTreePicker
                categories={categories}
                value={form.category_id || ""}
                onChange={(id, name) => update({ category_id: id, category_name: name || "" })}
                placeholder="Search or pick a category"
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Sale price (₹)">
              <input
                data-testid="input-selling-price"
                inputMode="decimal" value={form.selling_price ?? ""}
                onChange={(e) => update({ selling_price: e.target.value })}
                placeholder="What you charge"
                className="w-full h-11 rounded-xl border border-black/10 px-3 text-sm outline-none focus:border-neutral-900" />
            </Field>
            <Field label="MRP (₹)">
              <input
                data-testid="input-mrp"
                inputMode="decimal" value={form.mrp ?? ""}
                onChange={(e) => update({ mrp: e.target.value })}
                placeholder="Original / printed"
                className="w-full h-11 rounded-xl border border-black/10 px-3 text-sm outline-none focus:border-neutral-900" />
            </Field>
          </div>
          <div className="text-[10px] -mt-2" style={{ color: "var(--ink-3)" }}>
            Tip: <span className="font-medium">Sale price</span> is what the customer pays. MRP shows struck-through to highlight the discount.
          </div>

          <Field label="Description">
            <textarea
              data-testid="input-description"
              value={form.description || ""} onChange={(e) => update({ description: e.target.value })}
              rows={3}
              className="w-full rounded-xl border border-black/10 px-3 py-2 text-sm outline-none focus:border-neutral-900 resize-none"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Shade"><input data-testid="input-shade" value={form.shade || ""} onChange={(e) => update({ shade: e.target.value })} className="w-full h-11 rounded-xl border border-black/10 px-3 text-sm outline-none focus:border-neutral-900" /></Field>
            <Field label="Color"><input data-testid="input-color" value={form.color || ""} onChange={(e) => update({ color: e.target.value })} className="w-full h-11 rounded-xl border border-black/10 px-3 text-sm outline-none focus:border-neutral-900" /></Field>
            <Field label="Size"><input data-testid="input-size" value={form.size || ""} onChange={(e) => update({ size: e.target.value })} className="w-full h-11 rounded-xl border border-black/10 px-3 text-sm outline-none focus:border-neutral-900" /></Field>
            <Field label="Weight"><input data-testid="input-weight" value={form.weight || ""} onChange={(e) => update({ weight: e.target.value })} placeholder="e.g. 12g" className="w-full h-11 rounded-xl border border-black/10 px-3 text-sm outline-none focus:border-neutral-900" /></Field>
            <Field label="Net qty"><input data-testid="input-netqty" value={form.net_quantity || ""} onChange={(e) => update({ net_quantity: e.target.value })} className="w-full h-11 rounded-xl border border-black/10 px-3 text-sm outline-none focus:border-neutral-900" /></Field>
            <Field label="Batch no."><input data-testid="input-batch" value={form.batch_number || ""} onChange={(e) => update({ batch_number: e.target.value })} className="w-full h-11 rounded-xl border border-black/10 px-3 text-sm outline-none focus:border-neutral-900" /></Field>
            <Field label="Expiry"><input data-testid="input-expiry" value={form.expiry_date || ""} onChange={(e) => update({ expiry_date: e.target.value })} className="w-full h-11 rounded-xl border border-black/10 px-3 text-sm outline-none focus:border-neutral-900" /></Field>
            <Field label="Barcode"><input data-testid="input-barcode" value={form.barcode || ""} onChange={(e) => update({ barcode: e.target.value })} className="w-full h-11 rounded-xl border border-black/10 px-3 text-sm outline-none focus:border-neutral-900" /></Field>
            <Field label="SKU"><input data-testid="input-sku" value={form.sku || ""} onChange={(e) => update({ sku: e.target.value })} className="w-full h-11 rounded-xl border border-black/10 px-3 text-sm outline-none focus:border-neutral-900" /></Field>
            <Field label="Stock qty (empty = untracked)">
              <input
                data-testid="input-stock"
                inputMode="numeric"
                value={form.stock_quantity ?? ""}
                onChange={(e) => update({ stock_quantity: e.target.value })}
                placeholder="e.g. 3"
                className="w-full h-11 rounded-xl border border-black/10 px-3 text-sm outline-none focus:border-neutral-900"
              />
            </Field>
          </div>

          <div className="flex gap-4 items-center pt-1">
            <Toggle testid="toggle-featured" label="Featured" checked={!!form.featured} onChange={(v) => update({ featured: v })} />
            <Toggle testid="toggle-bestseller" label="Best seller" checked={!!form.best_seller} onChange={(v) => update({ best_seller: v })} />
            <Toggle testid="toggle-availability" label="In stock" checked={form.availability === "in_stock"} onChange={(v) => update({ availability: v ? "in_stock" : "out_of_stock" })} />
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Toggle testid="toggle-published" label="Published" checked={form.status === "published"} onChange={(v) => update({ status: v ? "published" : "draft" })} />
            <span className="text-[11px]" style={{ color: "var(--ink-3)" }}>{form.status === "published" ? "Visible on storefront" : "Saved as draft (hidden from customers)"}</span>
          </div>
        </div>

        <button
          data-testid="save-product-btn"
          onClick={save}
          disabled={saving}
          className="w-full h-13 py-4 rounded-full bg-neutral-900 text-white font-medium text-sm press disabled:opacity-60 mt-4"
        >
          {saving ? "Saving..." : (isEdit ? "Update Product" : "Save Product")}
        </button>
        <div className="h-8" />
      </div>
    </AppFrame>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

const Toggle = ({ label, checked, onChange, testid }) => (
  <button
    data-testid={testid}
    onClick={() => onChange(!checked)}
    className="flex items-center gap-2 text-xs press"
    type="button"
  >
    <span className={`w-9 h-5 rounded-full transition-colors ${checked ? "bg-neutral-900" : "bg-neutral-300"} relative`}>
      <span className={`absolute top-0.5 ${checked ? "left-4" : "left-0.5"} w-4 h-4 rounded-full bg-white transition-all`} />
    </span>
    <span className="text-neutral-700">{label}</span>
  </button>
);
